import { Fragment, ReactNode, useRef, useState } from "react";
import { useCheckForNewDocuments } from "api/documents/useGetDocumentCheck";
import { Portfolio, Representee, useGetContactInfo } from "api/initial/useGetContactInfo";
import classNames from "classnames";
import { Logo } from "components/Logo/Logo";
import { PortfolioSelect } from "components/PortfolioSelect/PortfolioSelect";
import { useMatchesBreakpoint } from "hooks/useMatchesBreakpoint";
import { useModifiedTranslation } from "hooks/useModifiedTranslation";
import { useGetContractIdData, SelectedContact } from "providers/ContractIdProvider";
import { useKeycloak } from "providers/KeycloakProvider";
import { NavLink, useNavigate } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import { initials } from "utils/initials";
import { VariantA, VariantB, VariantC, VariantD, VariantE } from "./ContactSwitcherVariants";
import { UserMenu } from "./UserMenu";

// ↓ Bytt til VariantB eller VariantC for å teste de andre variantene
const ActiveContactSwitcher = VariantA;

export const Navbar = () => {
  const isMd = useMatchesBreakpoint("lg");

  const { linkedContact, userProfile } = useKeycloak();
  const isCustomer = userProfile?.username?.toLowerCase()?.startsWith("f-") ?? false;

  const { data } = useGetContactInfo(false, linkedContact, true, !isCustomer);
  const { selectedContactId, setSelectedContactId, setSelectedContact } = useGetContractIdData();
  const navigate = useNavigate();

  const representees = data?.representees?.filter((x) => x.contactId.startsWith("F")) ?? [];
  const ownContact: SelectedContact = {
    id: data?.contactId,
    contactId: data?._contactId,
    userName: data?.name,
  };

  const handleContactSwitch = (contact: SelectedContact) => {
    setSelectedContactId(contact.id);
    setSelectedContact(contact);
    navigate("/overview");
  };

  return (
    <nav className="fixed top-0 left-0 margin h-[var(--navbar-height)] w-full bg-capBeige z-[1000]">
      <div id="capguard-navbar" className="w-full h-full flex flex-col justify-start">
        <div className="w-full h-full flex items-start pt-8">
          <div className="w-full flex-1 h-auto lg:h-12 flex justify-between items-center">
            <div className="flex justify-start items-center gap-4 sm:gap-6">
              <Logo />
              <PortfolioSelect />
            </div>
            <div className="flex flex-1 justify-end items-center gap-4">
              {isCustomer && representees.length > 0 && (
                <ActiveContactSwitcher
                  ownContact={ownContact}
                  representees={representees}
                  selectedContactId={selectedContactId}
                  onChange={handleContactSwitch}
                />
              )}
              <UserMenu />
            </div>
          </div>
        </div>
        <div className="w-full flex justify-start items-center border-b border-capGreen bg-capBeige gap-4 py-2 lg:py-4 lg:pb-8 overflow-x-scroll overflow-y-hidden no-scrollbar">
          {isMd && <NavItems />}
        </div>
      </div>
    </nav>
  );
};

export const isUserProfessional = (classification: string | undefined) => {
  return classification === "PROF";
};

export const hasEkb = (portfolios: Portfolio[]) => {
  return portfolios.some((pf) => pf.modelPortfolio?.name.includes("CAPGUARDEKB"));
};
const userIsAllowed = (username: string | undefined) => {
  return username?.toLocaleLowerCase().startsWith("inv") || false;
};

interface NavItemsProps {
  onClick?: () => void;
}
export const NavItems = ({ onClick }: NavItemsProps) => {
  const { t } = useModifiedTranslation();
  const { userProfile } = useKeycloak();
  const userIsInternal = userIsAllowed(userProfile?.username);
  const { selectedContactId } = useGetContractIdData();
  const { data, loading } = useGetContactInfo(false, selectedContactId);

  const userIsProfessional = isUserProfessional(data?.classification?.code);
  const userHasEkb = data && hasEkb(data.portfolios);

  const { data: latestNewDocumentTime } = useCheckForNewDocuments();

  const [dismissedAt, setDismissedAt] = useState<number>(() =>
    Number(sessionStorage.getItem("documents-dismissed-at") ?? 0)
  );

  const showDocumentsHighlight =
    latestNewDocumentTime !== false && latestNewDocumentTime > dismissedAt;

  const handleDocumentsClick = () => {
    if (showDocumentsHighlight) {
      const now = Date.now();
      sessionStorage.setItem("documents-dismissed-at", String(now));
      setDismissedAt(now);
    }
    onClick?.();
  };

  return (
    <>
      <NavElement onClick={onClick} to="overview">
        {t("navTab.tabs.overview")}
      </NavElement>
      <NavElement onClick={onClick} to="holdings">
        {t("navTab.tabs.holdings")}
      </NavElement>
      <NavElement onClick={onClick} to="transactions">
        {t("navTab.tabs.transactions")}
      </NavElement>
      <NavElement onClick={onClick} to="orders">
        {t("navTab.tabs.orders")}
      </NavElement>
      <NavElement
        onClick={handleDocumentsClick}
        to="documents"
        highlight={showDocumentsHighlight}
        tooltipContent={showDocumentsHighlight ? t("navTab.tabs.documentsTooltip") : undefined}
      >
        {t("navTab.tabs.documents")}
      </NavElement>
      {!userIsProfessional && (
        <NavElement onClick={onClick} to="profile">
          {t("navTab.tabs.profile")}
        </NavElement>
      )}

      <NavElement onClick={onClick} to="contact">
        {t("navTab.tabs.contact")}
      </NavElement>
      {userIsInternal && (
        <NavElement onClick={onClick} to="analysis">
          {t("navTab.tabs.analysis")}
        </NavElement>
      )}
      {userHasEkb && (
        <NavElement onClick={onClick} to="ekb">
          {t("navTab.tabs.ekb")}
        </NavElement>
      )}
    </>
  );
};

interface NavElementProps {
  to: string;
  children: ReactNode;
  onClick?: () => void;
  highlight?: boolean;
  tooltipContent?: string;
}
const NavElement = ({ to, children, onClick, highlight, tooltipContent }: NavElementProps) => {
  const tooltipId = `nav-highlight-${to}`;
  return (
    <NavLink
      to={to}
      onClick={onClick}
      data-tooltip-id={highlight && tooltipContent ? tooltipId : undefined}
      className={({ isActive }) =>
        classNames("cursor-pointer whitespace-nowrap relative", {
          "!font-extrabold underline": isActive,
        })
      }
    >
      {children}
      {highlight && (
        <>
          <span className="absolute -top-0.5 -right-1.5 w-2 h-2 rounded-full bg-capLightGreen" />
          {tooltipContent && (
            <Tooltip
              id={tooltipId}
              content={tooltipContent}
              place="bottom"
              positionStrategy="fixed"
            />
          )}
        </>
      )}
    </NavLink>
  );
};

const AVATAR_COLORS = [
  "#0A680F",
  "#3130ff",
  "#9F059F",
  "#003000",
  "#B60203",
  "#934100",
  "#5D5C00",
  "#00636B",
  "#660033",
  "#000066",
];

interface ContactSwitcherProps {
  ownContact: SelectedContact;
  representees: Representee[];
  selectedContactId: string | number | undefined;
  onChange: (contact: SelectedContact) => void;
}

const ContactSwitcher = ({
  ownContact,
  representees,
  selectedContactId,
  onChange,
}: ContactSwitcherProps) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const allContacts: SelectedContact[] = [
    ownContact,
    ...representees.map((r) => ({ id: r.id, contactId: r.contactId, userName: r.name })),
  ];

  const selected =
    allContacts.find((c) => String(c.id) === String(selectedContactId)) ?? ownContact;
  const selectedIndex = allContacts.findIndex((c) => String(c.id) === String(selectedContactId));
  const selectedColor = AVATAR_COLORS[Math.max(selectedIndex, 0) % AVATAR_COLORS.length];

  // Close on outside click
  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (!ref.current?.contains(e.relatedTarget as Node)) {
      setOpen(false);
    }
  };

  const handleSelect = (contact: SelectedContact) => {
    setOpen(false);
    onChange(contact);
  };

  return (
    <div ref={ref} className="relative" onBlur={handleBlur}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-full pl-1 pr-2.5 py-1 hover:bg-capGreen/10 transition-colors focus:outline-none"
      >
        <span
          className="w-7 h-7 leading-none rounded-full text-xs font-semibold text-white flex items-center justify-center shrink-0"
          style={{ backgroundColor: selectedColor }}
        >
          {initials(selected.userName ?? "")}
        </span>
        <span className="hidden sm:block text-sm font-medium text-capGreen max-w-[10rem] truncate">
          {selected.userName}
        </span>
        <svg
          className={classNames("w-3 h-3 text-capGreen/60 transition-transform", {
            "rotate-180": open,
          })}
          viewBox="0 0 12 12"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path d="M2 4l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-56 bg-capBeige border border-capGreen/20 rounded-lg shadow-lg z-[1100] py-1 overflow-hidden">
          <p className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-capGreen/50">
            Mine engasjement
          </p>
          {allContacts.map((contact, index) => {
            const isSelected = String(contact.id) === String(selectedContactId);
            const color = AVATAR_COLORS[index % AVATAR_COLORS.length];
            return (
              <button
                key={contact.id}
                onClick={() => handleSelect(contact)}
                className={classNames(
                  "w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors hover:bg-capGreen/10",
                  { "font-semibold": isSelected }
                )}
              >
                <span
                  className="w-6 h-6 rounded-full text-[10px] font-semibold text-white flex items-center justify-center shrink-0"
                  style={{ backgroundColor: color }}
                >
                  {initials(contact.userName ?? "")}
                </span>
                <span className="truncate text-capGreen">{contact.userName}</span>
                {isSelected && (
                  <svg
                    className="ml-auto w-3.5 h-3.5 text-capGreen shrink-0"
                    viewBox="0 0 14 14"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2.5}
                  >
                    <path d="M2 7l4 4 6-6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const InlineContactSwitcher = ({
  ownContact,
  representees,
  selectedContactId,
  onChange,
}: ContactSwitcherProps) => {
  const allContacts: SelectedContact[] = [
    ownContact,
    ...representees.map((r) => ({ id: r.id, contactId: r.contactId, userName: r.name })),
  ];

  return (
    <div className="flex items-center gap-1.5">
      {allContacts.map((contact, index) => {
        const isSelected = String(contact.id) === String(selectedContactId);
        const color = AVATAR_COLORS[index % AVATAR_COLORS.length];
        const tooltipId = `inline-rep-${contact.id}`;
        return (
          <Fragment key={contact.id}>
            <button
              id={tooltipId}
              onClick={() => onChange(contact)}
              style={{ backgroundColor: color }}
              className={classNames(
                "w-8 h-8 rounded-full text-xs font-semibold text-white flex items-center justify-center transition-all shrink-0",
                {
                  "ring-2 ring-offset-1 ring-capGreen scale-110": isSelected,
                  "opacity-50 hover:opacity-100": !isSelected,
                }
              )}
            >
              {initials(contact.userName ?? "")}
            </button>
            <Tooltip
              anchorSelect={`#${tooltipId}`}
              content={contact.userName}
              place="bottom"
              positionStrategy="fixed"
            />
          </Fragment>
        );
      })}
    </div>
  );
};
